import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PermissionConstants } from 'src/app/constants/permissions';
import { HasPermissionDirective } from '../../directives/has-permission.directive';
import { AboutInfo, AppVersionService } from '../../services/app-version.service';
import { LoggerService } from '../../services/logger.service';
import { SnackBarService } from '../../services/snack-service.service';

/**
 * "Tietoja sovelluksesta". Everyone sees the product, codename (when set), update date and
 * company, and can copy the technical block for support. The block itself (version, build)
 * is shown inline only to users who can see the roadmap or analytics.
 */
@Component({
  selector: 'ri-about-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatIconModule, TranslatePipe, HasPermissionDirective, RouterLink],
  templateUrl: './about-dialog.component.html',
  styleUrl: './about-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AboutDialogComponent {
  private readonly versions = inject(AppVersionService);
  private readonly snackBar = inject(SnackBarService);
  private readonly translate = inject(TranslateService);
  private readonly logger = inject(LoggerService);
  private readonly swUpdate = inject(SwUpdate);

  readonly roadmapPermission = [PermissionConstants.CanSeeRoadmap];
  readonly technicalPermissions = [PermissionConstants.CanSeeRoadmap, PermissionConstants.CanViewAnalytics];
  readonly about: AboutInfo = this.versions.aboutInfo(this.swUpdate.isEnabled);
  readonly year = this.versions.copyrightYear();

  async copyForSupport(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.versions.supportText(this.about));
      this.snackBar.success(this.translate.instant('ABOUT.COPIED'));
    } catch (e) {
      this.logger.errorWithContext('AboutDialog', 'Copy to clipboard failed', e);
      this.snackBar.error(this.translate.instant('ABOUT.COPY_FAILED'));
    }
  }
}
